import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { courseService } from '../services/apiService'

const CourseStudents = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [course, setCourse] = useState(null)
  const [students, setStudents] = useState([])
  const [stats, setStats] = useState({})

  const fetchData = useCallback(async () => {
    try {
      const [courseRes, studentsRes] = await Promise.all([
        courseService.getCourse(id),
        courseService.getCourseStudents(id),
      ])

      setCourse(courseRes.data)
      setStudents(studentsRes.data.students || [])
      setStats({
        totalStudents: studentsRes.data.total_students || 0,
        totalAssignments: studentsRes.data.total_assignments || 0,
        totalQuizzes: studentsRes.data.total_quizzes || 0,
      })
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-gray-600'>Loading...</p>
      </div>
    )
  }

  if (!course) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-red-600'>Course not found</p>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      {/* Header */}
      <div className='mb-8'>
        <Link
          to={`/courses/${id}/manage`}
          className='text-primary-600 hover:text-primary-700 mb-4 inline-block'
        >
          ← Back to Manage Course
        </Link>
        <h1 className='text-3xl font-bold text-gray-900'>{course.title}</h1>
        <p className='mt-2 text-gray-600'>Student Progress Overview</p>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>
            Total Students
          </div>
          <div className='mt-2 text-3xl font-bold text-primary-600'>
            {stats.totalStudents}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Assignments</div>
          <div className='mt-2 text-3xl font-bold text-orange-600'>
            {stats.totalAssignments}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Quizzes</div>
          <div className='mt-2 text-3xl font-bold text-green-600'>
            {stats.totalQuizzes}
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-900'>
            Enrolled Students
          </h2>
        </div>

        {students.length === 0 ? (
          <div className='px-6 py-12 text-center'>
            <p className='text-gray-500'>No students enrolled yet</p>
          </div>
        ) : (
          <div className='divide-y divide-gray-200'>
            {students.map((enrollment) => (
              <div
                key={enrollment.id}
                className='px-6 py-4 hover:bg-gray-50 transition'
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <h3 className='text-lg font-medium text-gray-900'>
                      {enrollment.student_name}
                    </h3>
                    <p className='text-sm text-gray-600'>
                      {enrollment.student.email}
                    </p>
                    <p className='text-xs text-gray-500 mt-1'>
                      Enrolled:{' '}
                      {new Date(enrollment.enrolled_at).toLocaleDateString()}
                    </p>
                  </div>

                  <div className='ml-6 flex items-center space-x-6'>
                    {/* Progress */}
                    <div className='text-center'>
                      <div className='text-2xl font-bold text-primary-600'>
                        {enrollment.progress}%
                      </div>
                      <div className='text-xs text-gray-500'>Progress</div>
                    </div>

                    {/* Status Badge */}
                    <div>
                      {enrollment.completed ? (
                        <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800'>
                          ✓ Completed
                        </span>
                      ) : enrollment.progress > 0 ? (
                        <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
                          In Progress
                        </span>
                      ) : (
                        <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800'>
                          Not Started
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className='mt-3'>
                  <div className='w-full bg-gray-200 rounded-full h-2'>
                    <div
                      className='bg-primary-600 h-2 rounded-full transition-all'
                      style={{ width: `${enrollment.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CourseStudents
